import { motion } from 'framer-motion';
import { Target, Eye, Heart, Users, Package, Truck, Headset } from 'lucide-react';
import { useSite } from '@/lib/SiteContext';

export default function AboutPage() {
  const { settings } = useSite();

  const values = [
    { icon: Target, title: 'Misyonumuz', desc: 'Bireysel ve kurumsal performansı sürekli geliştirerek müşterilerimize hızlı, ekonomik ve uzun ömürlü çözümler sunmak.' },
    { icon: Eye, title: 'Vizyonumuz', desc: 'Plastik sektöründeki yenilikçi yaklaşımımızla değişimlere en hızlı adapte olan, kalite standartlarını belirleyen öncü marka olmak.' },
    { icon: Heart, title: 'Değerlerimiz', desc: 'Güvenilirlik ilkesini kurum kültürümüzün merkezine koyarak, üretim kalitemizi her zaman en üst düzeyde tutmak.' },
    { icon: Users, title: 'Üretim Gücümüz', desc: 'Çocuk park gruplarından LED ışıklı tasarımlara kadar geniş bir yelpazede, kendi kalıplarımızla üretim yapan uzman bir ekibiz.' },
  ];

  return (
    <main data-testid="about-page" className="min-h-screen bg-brand-cream overflow-hidden">
      
      {/* 1. HERO - MANİFESTO BAŞLIĞI */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-pink/20 rounded-full blur-[100px] -z-10" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-blue/20 rounded-full blur-[100px] -z-10" />

        <div className="container mx-auto px-4 md:px-6 max-w-5xl relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="inline-block py-1 px-4 rounded-full bg-brand-dark/5 text-[10px] font-extrabold uppercase tracking-[0.3em] text-brand-dark/40 mb-8">
              Kurumsal Profil
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-black text-brand-dark tracking-tighter leading-[1.1] mb-8">
              Aracı Değil,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-dark via-brand-dark/80 to-slate-400">
                İmalatçı Firmayız.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-brand-dark/50 font-medium max-w-3xl mx-auto leading-relaxed">
              <strong className="text-brand-dark">Ayz Plastik</strong>, May Plastik üretim ve kalite güvencesiyle çocuk park gruplarından LED ışıklı plastik ürünlere kadar geniş bir yelpazede uzmanlaşmış, sektörün köklü üretici gücüdür.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. MARKA GÖRSELİ (Hardcoded URL yerine Favicon entegre edildi) */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative rounded-[3rem] overflow-hidden aspect-[16/7] md:aspect-[21/9] bg-brand-dark flex items-center justify-center shadow-2xl"
          >
            {/* Arka plan deseni/gradyanı */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-dark via-[#1a1c23] to-[#0f1015]" />
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
            
            {/* Ortalanmış Lüks Logo (Favicon) - FİLTRELER SİLİNDİ */}
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.8 }}
              src="/favicon.png" 
              alt="AyzPlastik İmalat"
              className="relative z-10 h-24 md:h-40 w-auto drop-shadow-2xl" 
            />
            
            {/* Alt Bilgi */}
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col md:flex-row justify-between items-end gap-6">
              <div>
                <h3 className="text-white text-2xl md:text-3xl font-black tracking-tight mb-2">
                  Üretim Gücümüz
                </h3>
                <p className="text-white/60 text-sm md:text-base max-w-xl font-medium leading-relaxed">
                  Rotasyon makineleri, kalıplar ve modern tesislerimizle en üst düzey kalite standartlarını doğrudan müşterimize yansıtıyoruz.
                </p>
              </div>
              <div className="shrink-0 hidden md:block">
                <div className="w-16 h-[1px] bg-white/30 mb-2" />
                <span className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em]">Est. 2026</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3. UZUN METİN (Bölünmüş Awwwards Tasarımı) */}
      <section className="py-24 bg-white rounded-t-[4rem] border-t border-slate-100 shadow-[0_-20px_60px_-15px_rgba(0,0,0,0.02)]">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
            <div className="md:col-span-5">
              <h2 className="text-3xl md:text-4xl font-black text-brand-dark tracking-tighter sticky top-32 leading-tight">
                Her İhtiyaca <br/>
                <span className="text-brand-dark/30">Uygun Çözümler.</span>
              </h2>
            </div>
            <div className="md:col-span-7 space-y-12">
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-lg md:text-xl text-brand-dark/70 font-medium leading-relaxed"
              >
                Amacımız, bireysel ve kurumsal performansı sürekli olarak geliştirmek ve müşterilerimize LED ışıklı ürünler alanında hızlı ve ekonomik çözümler sunmaktır. Yenilikçi yaklaşımımızla sektördeki değişimlere hızlı bir şekilde adapte olmaktayız.
              </motion.p>
              
              <div className="w-full h-[1px] bg-slate-100" />

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ space: true }}
                className="text-base text-brand-dark/60 leading-relaxed"
              >
                Üretim yelpazemizde; çocuk oyun ve park grupları, kaydıraklar, salıncaklar, donmaz suluklar, buzağı kulübeleri, plastik su depoları, rotasyon makineleri ve kalıpları, LED ışıklı çiçek saksıları, bistrolar, masa altlıkları, oturma grupları ve diğer dekoratif ürünler bulunmaktadır. Geniş ürün yelpazemizle, her türlü ihtiyaca kendi üretim bandımızdan çözümler sunmaktayız.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. DEĞERLERİMİZ KARTLARI */}
      <section className="py-24 bg-brand-cream border-t border-brand-dark/5">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((val, i) => (
              <motion.div
                key={val.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white p-8 rounded-[2rem] border border-slate-100 hover:shadow-xl transition-shadow duration-500 group"
              >
                <div className="w-14 h-14 mb-8 rounded-2xl bg-brand-blue/5 flex items-center justify-center group-hover:bg-brand-dark group-hover:text-white transition-colors duration-300">
                  <val.icon size={24} className="text-brand-dark group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-brand-dark text-xl mb-4 tracking-tight">
                  {val.title}
                </h3>
                <p className="text-brand-dark/50 text-sm leading-relaxed font-medium">
                  {val.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. OPERASYONEL İSTATİSTİKLER (Yeni Kartlar) */}
      <section className="py-24 bg-brand-dark text-white rounded-t-[4rem] relative overflow-hidden">
        {/* Arka plan süsleri */}
        <div className="absolute top-0 right-10 w-[300px] h-[300px] bg-brand-blue/10 rounded-full blur-[80px]" />
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Package, value: '50+', label: 'Kendi Üretimimiz Olan Ürün' },
              { icon: Truck, value: '81 İle', label: 'Hızlı ve Güvenilir Teslimat' },
              { icon: Headset, value: '7/24', label: 'Kesintisiz Müşteri Desteği' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex flex-col items-center text-center p-10 rounded-[2.5rem] bg-white/5 border border-white/10 hover:bg-white/10 transition-colors duration-300"
              >
                <div className="w-16 h-16 rounded-full bg-brand-blue/20 flex items-center justify-center mb-6">
                  <stat.icon size={28} className="text-brand-blue" />
                </div>
                <p className="text-4xl md:text-5xl font-black mb-2 tracking-tighter">
                  {stat.value}
                </p>
                <p className="text-white/50 text-sm font-bold uppercase tracking-widest">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}